import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePersonaSearch } from '@/roster/usePersonaSearch';
import type { Will } from '../estatesQueries';
import type { useWillMutations } from '../estatesQueries';

interface ExecutorEditorProps {
  will: Will;
  frozen: boolean;
  mutations: ReturnType<typeof useWillMutations>;
}

export function ExecutorEditor({ will, frozen, mutations }: ExecutorEditorProps) {
  const [query, setQuery] = useState('');
  // Race-safe debounced search (2026-07 audit): the old handler awaited
  // searchPersonas inline with no debounce or ordering guard.
  const { results: matches } = usePersonaSearch(query);

  return (
    <section className="space-y-2">
      <h3 className="text-xl font-semibold">Executors</h3>
      <ul className="space-y-1">
        {will.executors.map((executor) => (
          <li
            key={executor.id}
            className="flex items-center justify-between rounded border p-2 text-sm"
          >
            <span>{executor.persona_name}</span>
            {!frozen && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => mutations.removeExecutor.mutate(executor.id)}
              >
                Remove
              </Button>
            )}
          </li>
        ))}
        {will.executors.length === 0 && (
          <li className="text-sm text-muted-foreground">No executors named.</li>
        )}
      </ul>
      {!frozen && (
        <div className="space-y-2">
          <Input
            placeholder="Search characters…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {matches.length > 0 && (
            <ul className="rounded border text-sm">
              {matches.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className="w-full p-1 text-left hover:bg-accent"
                    onClick={() => {
                      mutations.addExecutor.mutate({ willId: will.id, personaId: m.id });
                      setQuery('');
                    }}
                  >
                    {m.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
